// uploadProposal.jsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import {
  createLawImplementationTargets,
  deleteLawImplementationTargets,
  updateAuditLawImplementation,
  updateLawImplementation,
  updateLawImplementationTargets,
} from "services/lawImplementationService";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { deleteLegalResearchTopic } from "services/legalResearchService";
import { updateLegislativeDrafting } from "services/legislativeService";
import {
  LAW_IMPLEMENTATION_REQUEST_STATUS,
  LEGAL_ADVISORY_REQUEST_STATUS,
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
} from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface uploadProposalProps {
  request: any;
  isOpen: boolean;
  onClose: () => void;
  setLegalResearch: Function;
  type?: string;
  onUploadProposal?: (data: {
    proposalSummary: string;
    proposalFile: string;
  }) => void;
}

const UploadDraftLaw: React.FC<uploadProposalProps> = ({
  request,
  isOpen,
  setLegalResearch,
  onUploadProposal,
  onClose,
  type,
}) => {
  const { t } = useTranslation();
  const [proposalSummary, setProposalSummary] = useState(
    request.proposal_summary || ""
  );
  const [proposalFile, setProposalFile] = useState<File | null>(null);
  const contextData = useAuth();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [target, setTarget] = useState("");
  const [targets, setTargets] = useState("");
  const [selectedTarget, setSelectedTarget] = useState("");
  const [selectedTargets, setSelectedTargets] = useState<any[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [team, setTeam] = useState<any | null>(null);
  const [prosecutorData, setProsecutor] = useState<any | null>(null);

  // Create a ref for the modal content div
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const userId = contextData.user.user_id;

    if (request?.teams) {
      // Find all teams where the current user is a prosecutor
      const userTeamEntries = request.teams.filter(
        (team: any) => team.prosecutors.some((p: any) => p.user_id === userId)
      );

      if (userTeamEntries.length > 0) {
        // For simplicity, taking the first team
        const firstTeamEntry = userTeamEntries[0];
        setTeam(firstTeamEntry);

        // Find the prosecutor entry for the current user
        const currentProsecutor = firstTeamEntry.prosecutors.find((p: any) => p.user_id === userId);
        setProsecutor(currentProsecutor);

        console.log("User belongs to teams:", userTeamEntries);
      }
    }

    console.log("formState: ", request);
  }, [request, contextData.user.user_id]);
  
  useEffect(() => {
    console.log("current: ", team);
    if (prosecutorData?.sectors) setTargets(prosecutorData.sectors);
  }, [team]);

  const handleRemoveCollector = (id: string) => {
    setSelectedTargets((prev: any) =>
      prev.filter((member: any) => member.id !== id)
    );
  };
  
  const handleDataCollectors = (value: string) => {
    setTarget(value);
  };
  
  const handleAddCollector = () => {
    if (target.trim() !== "") {
      setSelectedTargets((prev: any) => [
        ...prev,
        { id: Date.now().toString(), sector_name: target.trim() },
      ]);
      setTarget("");
    }
  };
  
  const getInitials = (str: string) =>
    str
      .split(" ")
      .map((s) => s[0])
      .join("")
      .toUpperCase();
      
  // Effect to handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setLegalResearch((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setSubmitting(true);

    try {
      let response: any;
      const data = new FormData();

      if (type === "target") {
        if (selectedTargets.length === 0) {
          setValidationError(t("PleaseAddAtLeastOneTarget") || "Please add at least one Target.");
          return;
        }

        // Check if user is a team leader in any team
        const isTeamLeader = request.teams.some((team: any) =>
          team.prosecutors.some((p: any) => p.user_id === contextData.user.user_id && p.role === "leader")
        );

        if (!isTeamLeader) {
          setValidationError(t("OnlyTeamLeadersCanAddTargets") || "Only team leaders can add targets.");
          return;
        }

        const createdSectors = [] as any[];
        for (const t of selectedTargets) {
          const s_data = new FormData();
          s_data.append("sector_name", t.sector_name);
          s_data.append("follow_up", request.id);

          const currentMember = request.identified_law_sectors?.find(
            (sector: any) => sector.id === t.id.toString()
          );

          if (currentMember) {
            s_data.append("followup_team", currentMember.followup_team);
            await updateLawImplementationTargets(t.id, s_data);
          } else {
            s_data.append("followup_team", prosecutorData.id);
            const created = await createLawImplementationTargets(s_data);
            createdSectors.push(created);
          }
        }

        // Update the state with created sectors
        setLegalResearch((prev: any) => ({
          ...prev,
          identified_law_sectors: [...(prev.identified_law_sectors || []), ...createdSectors]
        }));

        const s_data = new FormData();
        data.append(
          "current_approval_status",
          LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value
        );
        response = await updateLawImplementation(request.id, data);
      } else if (type === "legal") {
        data.append(
          "current_approval_status",
          LAW_IMPLEMENTATION_REQUEST_STATUS.audit_completed.value
        );
        data.append("audit_findings", proposalSummary);
        if (proposalFile) {
          data.append("audit_files", proposalFile);
        }
        response = await updateAuditLawImplementation(request.id, data);
      } else {
        data.append(
          "current_approval_status",
          LAW_IMPLEMENTATION_REQUEST_STATUS.proposal_submitted.value
        );
        data.append("enforcement_analysis_summary", proposalSummary);
        if (proposalFile) {
          data.append("enforcement_files", proposalFile);
        }
        response = await updateAuditLawImplementation(request.id, data);
      }
      setLegalResearch((prev: any) => ({
        ...prev,
        ...response,
      }));

      showToast(t("LawImplementationUploaded") || "Law Implementation Uploaded", "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUploadLawImplementation") || "Failed to upload Law Implementation.", "error");
    } finally {
      setSubmitting(false);
    }

    onUploadProposal?.({ proposalSummary, proposalFile: proposalFile ? proposalFile.name : "" });
    onClose();
  };

  const handleFileChange = (file: any) => {
    setProposalFile(file);
  };

  // Helper functions for dynamic text
  const getTitle = () => {
    if (type === "legal") {
      return t("Audit") || "Audit";
    } else if (type === "target") {
      return t("UploadIdentifiedLawOrSector") || "Upload Identified Law or Sector";
    }
    return t("UploadEnforcementAnalysis") || "Upload Enforcement Analysis";
  };

  const getSummaryLabel = () => {
    if (type === "legal") {
      return t("AuditSummary") || "Audit Summary";
    }
    return t("EnforcementAnalysisSummary") || "Enforcement Analysis Summary";
  };

  const getFileLabel = () => {
    if (type === "legal") {
      return t("AuditDocument") || "Audit Document*";
    }
    return t("EnforcementAnalysisDocument") || "Enforcement Analysis Document*";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex justify-end z-50 overflow-hidden">
      <div
        ref={modalContentRef}
        className="bg-white rounded-l-2xl shadow-2xl w-full max-w-xl p-8 relative flex flex-col h-full overflow-y-auto"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 hover:bg-gray-100 rounded-lg"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-8 pr-8">
          {getTitle()}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          {/* Scrollable Content Area */}
          <div className="flex-grow overflow-y-auto pr-2 space-y-6">
            {/* Target Section */}
            {type === "target" && (
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="target-input"
                    className="block text-sm font-semibold text-gray-800 mb-3"
                  >
                    {t("TargetName") || "Target Name"}
                  </label>
                  <div className="relative">
                    <input
                      id="target-input"
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg py-3 px-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all duration-200"
                      placeholder={t("EnterTargetNamePlaceholder") || "Enter target name"}
                      type="text"
                      value={target}
                      onChange={(e) => handleDataCollectors(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleAddCollector}
                  className="inline-flex items-center gap-2 text-[#AA232F] hover:text-[#8B1C26] font-medium text-sm transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t("AddTarget") || "Add Target"}
                </button>

                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">{t("AddedTargets") || "Added Targets"}</h3>
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1 scrollbar-hide">
                    {selectedTargets.length === 0 ? (
                      <div className="text-center py-8 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-500 font-medium">{t("NoTargetsAddedYet") || "No targets added yet"}</p>
                        <p className="text-xs text-gray-400 mt-1">{t("AddAtLeastOneTargetToContinue") || "Add at least one target to continue"}</p>
                      </div>
                    ) : (
                      selectedTargets.map((member: any) => (
                        <div
                          key={member.id}
                          className="group flex items-center bg-white border-2 border-gray-200 hover:border-gray-300 rounded-xl p-4 transition-all duration-200 hover:shadow-md"
                        >
                          <div className="w-11 h-11 rounded-full mr-3 bg-gradient-to-br from-gray-700 to-gray-900 text-white flex items-center justify-center font-bold text-sm select-none shadow-sm">
                            {getInitials(member.sector_name)}
                          </div>

                          <div className="flex-grow min-w-0">
                            <p className="font-semibold text-gray-900 truncate">
                              {member.sector_name}
                            </p>
                          </div>
                          
                          <button
                            type="button"
                            onClick={() => handleRemoveCollector(member.id)}
                            className="ml-3 p-2 text-gray-400 hover:text-[#AA232F] hover:bg-red-50 rounded-lg transition-all duration-200"
                            title={t("RemoveTarget") || "Remove target"}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Summary Section */}
            {type !== "target" && (
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="remark"
                    className="block text-sm font-semibold text-gray-800 mb-3"
                  >
                    {getSummaryLabel()}
                  </label>
                  <textarea
                    id="remark"
                    className="w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[120px] text-gray-900 placeholder-gray-500 transition-all duration-200"
                    placeholder={t("EnterDetailedSummaryPlaceholder") || "Enter detailed summary here..."}
                    value={proposalSummary}
                    onChange={(e) => setProposalSummary(e.target.value)}
                  ></textarea>
                </div>

                {/* File Upload Section */}
                <div>
                  <label
                    htmlFor="deadline"
                    className="block text-sm font-semibold text-gray-800 mb-3"
                  >
                    {getFileLabel()}
                  </label>
                  <div className="relative">
                    <FileUpload onChange={handleFileChange} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-[#AA232F] rounded-r-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-[#AA232F] mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm font-medium text-red-800">{validationError}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t-2 border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t("Submitting") || "Submitting..."}
                </span>
              ) : (
                t("Submit") || "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadDraftLaw;